/* =========================
   PEGUU — VALIDAR URL QR
   /pages/validar-url
========================= */

(() => {
  "use strict";

  const TOKEN_STORAGE_KEY = "peguu:qr-url-resolution-token";
  const PAGE_HANDLES = ["validar-url"];
  const API_BASE = "/api/qr-url-resolution";
  const OPTION_UPDATE = "UPDATE_URL";
  const OPTION_KEEP = "KEEP_URL";

  const isResolutionLocation = () => {
    const path = window.location.pathname.toLowerCase();

    return PAGE_HANDLES.some((handle) => path.includes(handle));
  };

  document.documentElement.classList.toggle(
    "peguu-is-qr-url-resolution",
    isResolutionLocation()
  );

  const resolveApiUrl = (path) => {
    if (window.PeguuApi?.resolve) {
      return window.PeguuApi.resolve(path);
    }

    return path;
  };

  const firstText = (...values) => {
    for (const value of values) {
      if (value == null || typeof value === "boolean") {
        continue;
      }

      if (typeof value === "object") {
        continue;
      }

      const text = String(value).trim();

      if (text && text !== "undefined" && text !== "null") {
        return text;
      }
    }

    return "";
  };

  const asObject = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return {};
    }

    return value;
  };

  const parseJson = async (response) => {
    const text = await response.text();

    if (!text) {
      return {};
    }

    try {
      return JSON.parse(text);
    } catch (error) {
      return {};
    }
  };

  const apiError = (payload, fallback) =>
    firstText(payload.error, payload.message) || fallback;

  const compactKey = (value) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, "_");

  const formatDateTime = (value) => {
    const text = firstText(value);

    if (!text) {
      return "";
    }

    const date = new Date(text);

    if (Number.isNaN(date.getTime())) {
      return text;
    }

    return new Intl.DateTimeFormat("es-MX", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  };

  const formatExpiresAt = (value) => {
    const formatted = formatDateTime(value);

    if (!formatted) {
      return "";
    }

    return `Este enlace vence el ${formatted}`;
  };

  const validationErrors = (data) => {
    const snapshot = asObject(data.validationSnapshot);
    const validation = asObject(snapshot.validation);
    const errors = Array.isArray(validation.errors) ? validation.errors : [];

    return errors
      .map((item) => firstText(item))
      .filter(Boolean);
  };

  const readStoredToken = () => {
    try {
      return firstText(sessionStorage.getItem(TOKEN_STORAGE_KEY));
    } catch (error) {
      return "";
    }
  };

  const storeToken = (token) => {
    if (!token) {
      return;
    }

    try {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch (error) {
      return;
    }
  };

  const getCleanPath = () => {
    const markedPath = firstText(
      document.querySelector("[data-peguu-qr-url-resolution]")
        ?.dataset.resolutionPath
    );

    if (markedPath) {
      return markedPath.startsWith("/") ? markedPath : `/${markedPath}`;
    }

    return "/pages/validar-url";
  };

  const hideTokenFromUrl = () => {
    const url = new URL(window.location.href);

    url.searchParams.delete("token");
    url.searchParams.delete("t");
    url.hash = "";

    const segments = url.pathname
      .replace(/\/+$/, "")
      .split("/")
      .filter(Boolean);
    const handleIndex = segments.findIndex((segment) =>
      PAGE_HANDLES.includes(segment.toLowerCase())
    );

    if (handleIndex >= 0) {
      url.pathname = `/${segments.slice(0, handleIndex + 1).join("/")}`;
    } else {
      url.pathname = getCleanPath();
    }

    const next = `${url.pathname}${url.search}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (current !== next) {
      history.replaceState({}, "", next);
    }
  };

  const getTokenFromLocation = () => {
    const url = new URL(window.location.href);
    const queryToken = firstText(
      url.searchParams.get("token"),
      url.searchParams.get("t")
    );
    const hashToken = firstText(
      url.hash.replace(/^#/, "").replace(/^token=/i, "")
    );
    const segments = url.pathname
      .replace(/\/+$/, "")
      .split("/")
      .filter(Boolean);
    const handleIndex = segments.findIndex((segment) =>
      PAGE_HANDLES.includes(segment.toLowerCase())
    );
    const pathToken =
      handleIndex >= 0 && segments[handleIndex + 1]
        ? decodeURIComponent(segments[handleIndex + 1])
        : "";
    const token = firstText(
      queryToken,
      pathToken,
      hashToken,
      readStoredToken()
    );

    if (token) {
      storeToken(token);
    }

    hideTokenFromUrl();

    return token;
  };

  const initialize = () => {
    if (!isResolutionLocation()) {
      return;
    }

    const root = document.querySelector("[data-peguu-qr-url-resolution]");

    if (!root || root.dataset.initialized === "true") {
      return;
    }

    root.dataset.initialized = "true";

    const finalTitle = root.querySelector("[data-final-title]");
    const finalCopy = root.querySelector("[data-final-copy]");
    const finalUrl = root.querySelector("[data-final-url]");
    const heading = root.querySelector("[data-order-heading]");
    const expiresNode = root.querySelector("[data-order-expires]");
    const errorsBlock = root.querySelector("[data-errors-block]");
    const errorsList = root.querySelector("[data-errors-list]");
    const failedUrlNode = root.querySelector("[data-failed-url]");
    const keepPreview = root.querySelector("[data-keep-url-preview]");
    const urlInput = root.querySelector("[data-new-url-input]");
    const updateStatus = root.querySelector("[data-update-status]");
    const keepStatus = root.querySelector("[data-keep-status]");
    const updateButton = root.querySelector("[data-update-url]");
    const keepButton = root.querySelector("[data-keep-url]");

    const orderFields = {
      orden: root.querySelector("[data-order-orden]"),
      odt: root.querySelector("[data-order-odt]"),
      cliente: root.querySelector("[data-order-cliente]")
    };

    let token = "";
    let isBusy = false;
    let failedDestinationUrl = "";

    const setView = (view) => {
      root.dataset.view = view;
      root.querySelectorAll("[data-view-panel]").forEach((panel) => {
        panel.hidden = panel.dataset.viewPanel !== view;
      });
    };

    const setStatus = (node, message, state = "") => {
      if (!node) {
        return;
      }

      node.textContent = message;
      node.hidden = !message;
      node.dataset.state = state;
    };

    const showFinal = ({ title, copy, url = "" }) => {
      if (finalTitle) {
        finalTitle.textContent = title;
      }

      if (finalCopy) {
        finalCopy.textContent = copy;
      }

      if (finalUrl) {
        finalUrl.textContent = url;
        finalUrl.hidden = !url;
      }

      setView("final");
    };

    const byTokenUrl = (suffix = "") => {
      const encodedToken = encodeURIComponent(token);
      const extra = suffix ? `/${suffix.replace(/^\/+/, "")}` : "";

      return resolveApiUrl(
        `${API_BASE}/by-token/${encodedToken}${extra}`
      );
    };

    const updateButtons = () => {
      if (updateButton) {
        updateButton.disabled = isBusy;
        updateButton.textContent = isBusy ? "Enviando…" : "Enviar nueva URL";
      }

      if (keepButton) {
        keepButton.disabled = isBusy;
        keepButton.textContent = isBusy
          ? "Enviando…"
          : "Seguir con esta URL";
      }

      if (urlInput) {
        urlInput.disabled = isBusy;
      }
    };

    const paintCase = (data) => {
      const order = asObject(data.orderSnapshot);
      const values = {
        orden: firstText(order.orden, order.orderNumber),
        odt: firstText(order.odt),
        cliente: firstText(order.cliente, order.guestName)
      };

      Object.entries(values).forEach(([key, value]) => {
        if (orderFields[key]) {
          orderFields[key].textContent = value || "—";
        }
      });

      failedDestinationUrl = firstText(data.failedDestinationUrl);

      if (failedUrlNode) {
        failedUrlNode.textContent = failedDestinationUrl || "—";
      }

      if (keepPreview) {
        keepPreview.textContent = failedDestinationUrl || "";
        keepPreview.hidden = !failedDestinationUrl;
      }

      if (heading) {
        const folio = firstText(values.odt, values.orden);
        const client = values.cliente;

        heading.textContent =
          folio && client
            ? `Pedido ${folio} — ${client}`
            : "No pudimos acceder a la URL de tu calcomanía QR. Elige cómo continuar.";
      }

      const errors = validationErrors(data);

      if (errorsList) {
        errorsList.innerHTML = "";

        const items = errors.length
          ? errors
          : ["No pudimos acceder a la URL"];

        items.forEach((error) => {
          const item = document.createElement("li");
          item.textContent = error;
          errorsList.appendChild(item);
        });
      }

      if (errorsBlock) {
        errorsBlock.hidden = false;
      }

      const expiresText = formatExpiresAt(data.expiresAt);

      if (expiresNode) {
        expiresNode.textContent = expiresText;
        expiresNode.hidden = !expiresText;
      }
    };

    const showResolved = (data) => {
      const selected = compactKey(data.selectedOption);
      const destination = firstText(
        data.newDestinationUrl,
        data.destinationUrl,
        data.failedDestinationUrl
      );

      if (selected === OPTION_UPDATE) {
        showFinal({
          title: "URL actualizada",
          copy: "Guardamos tu nueva URL. La revisamos de nuevo y te avisamos.",
          url: destination
        });
        return;
      }

      showFinal({
        title: "Seguimos con esta URL",
        copy: "Seguimos con esta URL. El equipo la confirma a mano.",
        url: destination
      });
    };

    const loadCase = async () => {
      token = getTokenFromLocation();

      if (!token) {
        showFinal({
          title: "Este enlace no está disponible",
          copy: "No encontramos el enlace de esta validación. Revisa el correo o el mensaje y vuelve a entrar."
        });
        return;
      }

      try {
        const response = await fetch(byTokenUrl(), {
          method: "GET",
          headers: {
            Accept: "application/json"
          }
        });
        const payload = await parseJson(response);
        const data = asObject(payload.data || payload);

        if (!response.ok || payload?.success === false) {
          const message = apiError(payload, "Este enlace no está disponible.");

          if (response.status === 404 || /no encontrado/i.test(message)) {
            showFinal({
              title: "Enlace inválido",
              copy: "No encontramos este enlace de validación."
            });
            return;
          }

          if (response.status === 400 && /expir/i.test(message)) {
            showFinal({
              title: "Enlace vencido",
              copy: "Este enlace venció. Escríbenos a Peguu para continuar."
            });
            return;
          }

          showFinal({
            title: "Este enlace no está disponible",
            copy: message
          });
          return;
        }

        const status = compactKey(data.status);

        if (status === "EXPIRED") {
          showFinal({
            title: "Enlace vencido",
            copy: "Este enlace venció. Escríbenos a Peguu para continuar."
          });
          return;
        }

        if (status === "RESOLVED") {
          showResolved(data);
          return;
        }

        if (status === "OPEN" || status === "SELECTED") {
          paintCase(data);
          setView("form");
          updateButtons();
          return;
        }

        showFinal({
          title: "Este enlace no está disponible",
          copy: "El enlace para validar la URL del QR está usado, vencido o inactivo."
        });
      } catch (error) {
        showFinal({
          title: "No pudimos cargar la validación",
          copy: "Inténtalo más tarde."
        });
      }
    };

    const confirmOption = async (body) => {
      const response = await fetch(byTokenUrl("confirm"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      });
      const payload = await parseJson(response);

      if (!response.ok || payload?.success === false) {
        const error = new Error(
          apiError(payload, "No pudimos completar la acción. Inténtalo de nuevo.")
        );
        error.status = response.status;
        throw error;
      }

      return payload;
    };

    const handleAlreadyResolved = async () => {
      try {
        const response = await fetch(byTokenUrl(), {
          method: "GET",
          headers: {
            Accept: "application/json"
          }
        });
        const payload = await parseJson(response);
        const data = asObject(payload.data || payload);

        if (response.ok && compactKey(data.status) === "RESOLVED") {
          showResolved(data);
          return true;
        }
      } catch (error) {
        return false;
      }

      return false;
    };

    const handleUpdateUrl = async () => {
      if (isBusy) {
        return;
      }

      const newDestinationUrl = firstText(urlInput?.value);

      if (!newDestinationUrl) {
        setStatus(
          updateStatus,
          "Escribe la nueva URL destino.",
          "error"
        );
        return;
      }

      isBusy = true;
      updateButtons();
      setStatus(updateStatus, "Enviando URL…", "loading");

      try {
        const payload = await confirmOption({
          option: OPTION_UPDATE,
          newDestinationUrl
        });
        const data = asObject(payload.data || payload);

        showFinal({
          title: "URL actualizada",
          copy: "Guardamos tu nueva URL. La revisamos de nuevo y te avisamos.",
          url: firstText(data.destinationUrl, newDestinationUrl)
        });
      } catch (error) {
        const message = String(error?.message || "");

        if (/expir/i.test(message)) {
          showFinal({
            title: "Enlace vencido",
            copy: "Este enlace venció. Escríbenos a Peguu para continuar."
          });
          return;
        }

        if (/ya no está disponible/i.test(message)) {
          const resolved = await handleAlreadyResolved();

          if (!resolved) {
            showFinal({
              title: "Este enlace ya no está disponible",
              copy: message
            });
          }

          return;
        }

        setStatus(
          updateStatus,
          message || "No pudimos guardar la URL. Inténtalo de nuevo.",
          "error"
        );
      } finally {
        isBusy = false;
        updateButtons();
      }
    };

    const handleKeepUrl = async () => {
      if (isBusy) {
        return;
      }

      isBusy = true;
      updateButtons();
      setStatus(keepStatus, "Enviando…", "loading");

      try {
        const payload = await confirmOption({
          option: OPTION_KEEP
        });
        const data = asObject(payload.data || payload);

        showFinal({
          title: "Seguimos con esta URL",
          copy: "Seguimos con esta URL. El equipo la confirma a mano.",
          url: firstText(data.destinationUrl, failedDestinationUrl)
        });
      } catch (error) {
        const message = String(error?.message || "");

        if (/expir/i.test(message)) {
          showFinal({
            title: "Enlace vencido",
            copy: "Este enlace venció. Escríbenos a Peguu para continuar."
          });
          return;
        }

        if (/ya no está disponible/i.test(message)) {
          const resolved = await handleAlreadyResolved();

          if (!resolved) {
            showFinal({
              title: "Este enlace ya no está disponible",
              copy: message
            });
          }

          return;
        }

        setStatus(
          keepStatus,
          message || "No pudimos guardar tu elección. Inténtalo de nuevo.",
          "error"
        );
      } finally {
        isBusy = false;
        updateButtons();
      }
    };

    urlInput?.addEventListener("input", () => {
      setStatus(updateStatus, "", "");
    });

    updateButton?.addEventListener("click", (event) => {
      event.preventDefault();
      void handleUpdateUrl();
    });

    keepButton?.addEventListener("click", (event) => {
      event.preventDefault();
      void handleKeepUrl();
    });

    urlInput?.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        void handleUpdateUrl();
      }
    });

    void loadCase();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, {
      once: true
    });
  } else {
    initialize();
  }
})();
